import ReconnectingWebSocket from 'reconnecting-websocket';

const NOTIFICATIONS_UPDATED_EVENT = 'autoflow:notifications-updated';
const ACTIVE_CUSTOMERS_DASHBOARD_UPDATED_EVENT = 'autoflow:active-customers-dashboard-updated';
const TEXT_MANAGER_MESSAGE_RECEIVED_EVENT = 'autoflow:text-manager-message-received';

let url,
  options,
  connected = false;
let reconnectAttempts;

const getSocketState = () => window.socket?.readyState;

const sendSocketMessage = (params) => {
  const message = JSON.stringify(params);

  if (getSocketState() === 1) {
    window.socket.send(message);
    return;
  }

  const sendOnOpen = () => {
    window.socket.send(message);
    window.socket.removeEventListener?.('open', sendOnOpen);
  };

  if (typeof window.socket?.addEventListener === 'function') {
    window.socket.addEventListener('open', sendOnOpen);
    return;
  }

  setTimeout(() => {
    if (getSocketState() === 1) {
      window.socket.send(message);
    }
  }, 250);
};

const initializeConnection = (wss_url, masterConfig, user, toggle) => {
  console.log(masterConfig);
  options = {
    shop_id: masterConfig.shop_id,
    user_id: user.id,
    access: user.level_access,
    tz: masterConfig.time_zone,
    dvi: masterConfig.inspect_addon ? 1 : 0,
    qc: masterConfig.qc_form_addon ? 1 : 0,
    wo: masterConfig.workorder_addon ? 1 : 0,
    warranty: masterConfig.mode == 6 ? 1 : 0,
    mso_id: masterConfig.mso_id,
    integration: masterConfig.integration_type,
  };

  url = wss_url + '?' + $.param(options);
  reconnectAttempts = 0;

  window.socket = new ReconnectingWebSocket(url);
  window.socket.debug = false;
  window.socket.debugAll = false;
  window.socket.maxReconnectAttempts = 5;
  window.socket.timeoutInterval = 5400;
  window.socketmaxReconnectInterval = 120000;

  window.socket.onopen = function (event) {
    if (reconnectAttempts === 5) {
      window.socket.close();
    } else {
      connected = true;
      reconnectAttempts++;
    }
  };

  window.socket.onmessage = function (event) {
    let data = JSON.parse(event.data);
    channelMessage(data);
  };

  window.socket.onclose = function (event) {
    console.log('WSS Conneciton closed!');
  };
};

const dispatchActiveCustomersDashboardUpdated = (payload = {}) => {
  window.dispatchEvent(
    new CustomEvent(ACTIVE_CUSTOMERS_DASHBOARD_UPDATED_EVENT, {
      detail: payload,
    }),
  );
};

const parseSocketResults = (results) => {
  if (typeof results !== 'string') {
    return results ?? {};
  }

  try {
    return JSON.parse(results);
  } catch (error) {
    console.warn('Failed to parse socket results', error);
    return {};
  }
};

const channelMessage = (data) => {
  const rawChannelId = data.channelId ?? data.channelID ?? data.channelid ?? data.name;
  const channelId = typeof rawChannelId === 'string' ? rawChannelId.toUpperCase() : rawChannelId;

  switch (channelId) {
    case 'NOTIFY':
      let results = parseSocketResults(data.results) ?? {};
      inboundNotifications(results.notifications ?? results.notification ?? results);
      break;

    case 'WALL':
      inboundWallMessages(data.results);
      break;

    case 'ACTIVECUSTOMER': {
      const results = parseSocketResults(data.results);
      dispatchActiveCustomersDashboardUpdated(results);
      break;
    }

    default:
      console.log(data);
      break;
  }
};

const inboundWallMessages = (list) => {
  if (window.wall_messages === undefined) {
    window.wall_messages = [];
  }
  window.wall_messages = window.wall_messages.concat(list);
};

const dispatchNotificationsUpdated = () => {
  window.dispatchEvent(
    new CustomEvent(NOTIFICATIONS_UPDATED_EVENT, {
      detail: [...(window.notifications ?? [])],
    }),
  );
};

const dispatchTextManagerMessageReceived = (notifications = []) => {
  const messages = notifications.filter(
    (notification) =>
      ['notify_cust_reply', 'notify_inbound_marketing_response'].includes(
        notification?.notify,
      ) && notification?.method === 'add',
  );

  if (!messages.length) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(TEXT_MANAGER_MESSAGE_RECEIVED_EVENT, {
      detail: messages,
    }),
  );
};

const inboundNotifications = (list) => {
  const currentNotifications = Array.isArray(window.notifications) ? window.notifications : [];
  const currentNotificationKeys = new Set(
    currentNotifications
      .map((notification) =>
        notification && notification.id !== undefined
          ? `${notification.id}|${notification.notify}`
          : null,
      )
      .filter(Boolean),
  );
  const notifications = (Array.isArray(list) ? list : [list].filter(Boolean)).filter(
    (notification) => {
      const key =
        notification && notification.id !== undefined
          ? `${notification.id}|${notification.notify}`
          : null;

      if (!key) {
        return true;
      }

      if (currentNotificationKeys.has(key)) {
        return false;
      }

      currentNotificationKeys.add(key);

      return true;
    },
  );

  window.notifications = currentNotifications.concat(notifications);
  dispatchNotificationsUpdated();
  dispatchTextManagerMessageReceived(notifications);
};

const getWallMessageInQueue = (wss_url, masterConfig, user, toggle) => {
  // No websocket is configured in the local Docker stack (WSS_URL is empty).
  if (!wss_url) {
    return;
  }

  if (window.socket === undefined) {
    initializeConnection(wss_url, masterConfig, user, toggle);
  }

  let params = {};
  params.action = 'wallMessage';
  params.user_id = user.id;
  params.shop_id = masterConfig.shop_id;
  params.integration = masterConfig.integration_type;
  params.tz = masterConfig.time_zone;
  params.qc = masterConfig.qc_form_addon ? 1 : 0;
  params.mso_id = masterConfig.mso_id;
  params.level_access = user.level_access;
  params.dvi = masterConfig.inspect_addon ? 1 : 0;
  params.wo = masterConfig.workorder_addon ? 1 : 0;
  params.warranty = masterConfig.mode == 6 ? 1 : 0;

  sendSocketMessage(params);
};

const subscribeACDashboard = (wss_url, masterConfig, user, toggle = {}) => {
  if (!wss_url || !masterConfig || !user) {
    console.warn('Missing required parameters for WebSocket initialization');
    return;
  }

  if (window.socket === undefined) {
    initializeConnection(wss_url, masterConfig, user, toggle);
  }

  let params = {};
  params.action = 'subscribeChannel';
  params.channelId = 'ActiveCustomer';
  params.shop_id = masterConfig.shop_id;
  params.user_id = user.id;

  sendSocketMessage(params);
};

const getNotificationInQueue = (wss_url, masterConfig, user, toggle = {}) => {
  if (!wss_url || !masterConfig || !user) {
    console.warn('Missing required parameters for WebSocket initialization');
    return;
  }

  if (window.socket === undefined) {
    initializeConnection(wss_url, masterConfig, user, toggle);
  }

  let data = { topics: [] };

  if (user.websocket_topics) {
    Object.keys(user.websocket_topics).forEach((key) => {
      data.topics.push(user.websocket_topics[key]);
    });
  }

  let params = {};
  params.shop_id = masterConfig.shop_id;
  params.user_id = user.id;
  params.time_zone = masterConfig.time_zone;
  params.details = JSON.stringify(data);
  params.single_queue_notification = masterConfig.single_notification_queue;
  params.retention = toggle?.notifications_retention ?? 7;
  params.action = 'notify';
  params.method = 'inqueue';

  sendSocketMessage(params);
};

export {
  ACTIVE_CUSTOMERS_DASHBOARD_UPDATED_EVENT,
  NOTIFICATIONS_UPDATED_EVENT,
  TEXT_MANAGER_MESSAGE_RECEIVED_EVENT,
  getWallMessageInQueue,
  initializeConnection,
  getNotificationInQueue,
  subscribeACDashboard,
};
